/* Native enhancement of FINAL rendered markup, including ordered-product adapter.
 * Dawn ProductModal needs data-media-id and custom galleries have none; use one
 * shared native dialog, never intercept Dawn's own media openers or commerce.
 */
(() => {
  if (window.astrobrewViewportFirst) return;
  window.astrobrewViewportFirst = true;
  const start = () => {
    if (!('showModal' in HTMLDialogElement.prototype)) return;
    // This asset is loaded by affected sections only. Match Canvas's complete
    // main-content eligibility, including collection/editorial photography.
    const scope = 'main';
    const excluded = 'a,button,modal-opener,product-modal,dialog,media-gallery,.product__media,.product-media-container,[data-media-id],[data-product-media],.ab-home-hero,.ah-hero,.ah-shop,.ah-coffee-strip,.ab-home-coffee-strip,.ab-home-shop,[hidden],[aria-hidden="true"],.slider-media:not(.active)';
    const enhanced = new Map();
    const dialog = document.createElement('dialog');
    dialog.className = 'ab-vf-dialog';
    dialog.setAttribute('aria-label', 'Image detail');
    dialog.innerHTML = '<header><h2>Image detail</h2><button type="button" data-vf-close autofocus>Close</button></header><div class="ab-vf-dialog-tools"><button type="button" data-vf-prev>Previous</button><button type="button" data-vf-zoom aria-pressed="false">Zoom in</button><button type="button" data-vf-next>Next</button></div><div class="ab-vf-dialog-stage" tabindex="0" aria-label="Complete image fitted to viewport."><img alt=""><p data-vf-error hidden role="status">This image could not load. Close and try another view.</p></div><p class="ab-vf-dialog-caption" aria-live="polite"></p><div class="ab-vf-dialog-copy" tabindex="0" hidden></div>';
    const stage = dialog.querySelector('.ab-vf-dialog-stage');
    const displayed = stage.querySelector('img');
    const copy = dialog.querySelector('.ab-vf-dialog-copy');
    const caption = dialog.querySelector('.ab-vf-dialog-caption');
    const tools = dialog.querySelector('.ab-vf-dialog-tools');
    const zoom = dialog.querySelector('[data-vf-zoom]');
    const error = dialog.querySelector('[data-vf-error]');
    let opener, images = [], index = 0, lockedOverflow = '', lockedPadding = '', zoomed = false;
    const visible = element => !!element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden';
    const record = img => ({
      src: img.currentSrc || img.src,
      alt: img.alt,
      caption: img.closest('figure')?.querySelector('figcaption')?.textContent ||
        img.parentElement.querySelector('.p-photo-label,.pz-photo-label,.abpd-photo-label')?.textContent || img.alt
    });
    const eligible = img => img.alt && img.closest(scope) && !img.closest(excluded);
    const enhance = () => {
      const main = document.querySelector('main');
      if (!main) return;
      main.querySelectorAll('img').forEach(img => {
        if (!eligible(img)) {
          const original = enhanced.get(img);
          if (original) {
            enhanced.delete(img);
            original.forEach(([name, value]) => value === null ? img.removeAttribute(name) : img.setAttribute(name, value));
            img.classList.remove('ab-vf-image-trigger');
          }
          return;
        }
        if (!enhanced.has(img)) {
          enhanced.set(img, ['role', 'tabindex', 'aria-label'].map(name => [name, img.getAttribute(name)]));
          img.setAttribute('role', 'button');
          img.tabIndex = 0;
          img.classList.add('ab-vf-image-trigger');
        }
        const label = `Enlarge image: ${img.alt}`;
        if (img.getAttribute('aria-label') !== label) img.setAttribute('aria-label', label);
      });
      main.querySelectorAll('[data-ab-vf-description] summary').forEach(summary => {
        summary.setAttribute('aria-haspopup', 'dialog');
      });
      for (const img of enhanced.keys()) if (!img.isConnected) enhanced.delete(img);
    };
    const fit = () => {
      zoomed = false;
      stage.classList.remove('is-zoomed');
      stage.scrollTop = stage.scrollLeft = 0;
      stage.setAttribute('aria-label', 'Complete image fitted to viewport.');
      zoom.textContent = 'Zoom in';
      zoom.setAttribute('aria-pressed', 'false');
    };
    const showImage = () => {
      fit();
      error.hidden = true;
      const image = images[index];
      displayed.src = image.src;
      displayed.alt = image.alt;
      caption.textContent = image.caption || image.alt;
    };
    const open = target => {
      if (!dialog.isConnected) document.body.append(dialog);
      opener = target;
      lockedOverflow = document.body.style.overflow;
      lockedPadding = document.body.style.paddingRight;
      const gutter = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      if (gutter) document.body.style.paddingRight = `${parseFloat(getComputedStyle(document.body).paddingRight) + gutter}px`;
      document.body.style.overflow = 'hidden';
      dialog.showModal();
      dialog.querySelector('[data-vf-close]').focus();
    };
    const close = () => {
      if (dialog.open) dialog.close();
    };
    dialog.addEventListener('close', () => {
      document.body.style.overflow = lockedOverflow;
      document.body.style.paddingRight = lockedPadding;
      copy.replaceChildren();
      fit();
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    });
    dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
    const navigate = direction => {
      if (!images.length || stage.hidden) return;
      index = (index + direction + images.length) % images.length;
      showImage();
    };
    dialog.addEventListener('click', event => {
      if (event.target.closest('[data-vf-close]')) close();
      if (event.target.closest('[data-vf-prev]')) navigate(-1);
      if (event.target.closest('[data-vf-next]')) navigate(1);
      if (event.target.closest('[data-vf-zoom]')) {
        zoomed = !zoomed;
        stage.classList.toggle('is-zoomed', zoomed);
        zoom.textContent = zoomed ? 'Fit image' : 'Zoom in';
        zoom.setAttribute('aria-pressed', String(zoomed));
        stage.setAttribute('aria-label', zoomed ? 'Zoomed image. Scroll or swipe to inspect.' : 'Complete image fitted to viewport.');
        if (!zoomed) stage.scrollTop = stage.scrollLeft = 0;
      }
    });
    displayed.addEventListener('error', () => { error.hidden = false; });
    dialog.addEventListener('keydown', event => {
      if (event.key === 'Tab') {
        const focusable = [...dialog.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])')].filter(visible);
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
          event.preventDefault(); first?.focus();
        }
      }
      if (!stage.hidden && !zoomed && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault(); navigate(event.key === 'ArrowLeft' ? -1 : 1);
      }
    });
    let touchStart;
    stage.addEventListener('touchstart', event => {
      touchStart = event.touches.length === 1 ? [event.touches[0].clientX, event.touches[0].clientY] : null;
    }, { passive: true });
    stage.addEventListener('touchend', event => {
      if (!zoomed && touchStart && event.changedTouches.length === 1) {
        const dx = event.changedTouches[0].clientX - touchStart[0];
        const dy = event.changedTouches[0].clientY - touchStart[1];
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) navigate(dx < 0 ? 1 : -1);
      }
      touchStart = null;
    }, { passive: true });
    const activate = event => {
      if (dialog.open || (event.type === 'keydown' && !['Enter', ' '].includes(event.key))) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const summary = target.closest('[data-ab-vf-description] summary');
      if (summary) {
        event.preventDefault();
        dialog.setAttribute('aria-label', summary.textContent.trim());
        dialog.querySelector('h2').textContent = summary.textContent.trim().replace(/^Read more about/, 'About');
        stage.hidden = tools.hidden = caption.hidden = true;
        copy.hidden = false;
        copy.replaceChildren(...[...summary.parentElement.querySelector('.ab-vf-additional-copy').childNodes].map(node => node.cloneNode(true)));
        open(summary);
        return;
      }
      if (!(target instanceof HTMLImageElement) || !eligible(target) || !visible(target)) return;
      event.preventDefault();
      const gallery = target.closest('.p-order-gallery,.pz-order-gallery,.abpd-gallery');
      const thumbs = gallery ? [...gallery.querySelectorAll('.p-thumbs button,.pz-thumbs button,.abpd-thumbnail')] : [];
      if (thumbs.length) {
        images = thumbs.map(button => {
          const img = button.querySelector('img');
          return {
            src: button.dataset.galleryImage || img.src,
            alt: button.dataset.galleryAlt || img.alt || button.getAttribute('aria-label') || target.alt,
            caption: button.dataset.galleryCaption || button.getAttribute('aria-label') || target.alt
          };
        });
        index = thumbs.findIndex(button => button.classList.contains('active') || button.getAttribute('aria-pressed') === 'true');
        index = Math.max(0, index);
      } else {
        const sequence = [...document.querySelectorAll('main .ab-vf-image-trigger')].filter(img => eligible(img) && visible(img));
        images = sequence.map(record);
        index = Math.max(0, sequence.indexOf(target));
      }
      dialog.setAttribute('aria-label', 'Enlarged image');
      dialog.querySelector('h2').textContent = 'Image detail';
      stage.hidden = tools.hidden = caption.hidden = false;
      copy.hidden = true;
      showImage();
      open(target);
    };
    document.addEventListener('click', activate);
    document.addEventListener('keydown', activate);
    document.addEventListener('shopify:section:load', enhance);
    document.addEventListener('shopify:section:unload', event => {
      if (opener && event.target.contains(opener)) close();
    });
    /* Keep the existing filter controller and its cards untouched. Only repack
     * visible cards into the same genuine four-product rows used by Canvas. */
    const assortments = new WeakSet();
    const setupAssortments = () => document.querySelectorAll('astrobrew-assortment').forEach(root => {
      if (assortments.has(root)) return;
      assortments.add(root);
      const grid = root.querySelector('.ab-catalog-grid');
      if (!grid) return;
      const cards = [...grid.querySelectorAll('[data-category]')];
      const regroup = () => {
        const visibleCards = cards.filter(card => !card.hidden);
        const fragment = document.createDocumentFragment();
        for (let i = 0; i < visibleCards.length; i += 4) {
          const row = document.createElement('section');
          row.className = 'ab-catalog-row';
          row.setAttribute('aria-label', `AstroBrew products row ${i / 4 + 1}`);
          visibleCards.slice(i, i + 4).forEach(card => row.append(card));
          fragment.append(row);
        }
        const hiddenCards = document.createElement('div');
        hiddenCards.hidden = true;
        cards.filter(card => card.hidden).forEach(card => hiddenCards.append(card));
        fragment.append(hiddenCards);
        grid.replaceChildren(fragment);
      };
      new MutationObserver(regroup).observe(grid, {
        subtree: true, attributes: true, attributeFilter: ['hidden']
      });
      regroup();
    });
    setupAssortments();
    document.addEventListener('shopify:section:load', setupAssortments);
    /* Generic ProductTemplate already has a Dawn ProductModal. Enhance that
     * instance rather than wiring its modal-opener into our custom-gallery
     * dialog. Native media IDs, selection, video/model rendering and hide/show
     * methods remain owned by Dawn. */
    const dawnModals = new WeakSet();
    const setupDawnModals = () => document.querySelectorAll('.ab-canvas-product-template[data-section]').forEach(root => {
      // ModalDialog.connectedCallback reparents the existing modal to body.
      // Resolve only this approved section's modal by its stable Dawn ID.
      const modal = document.getElementById(`ProductModal-${root.dataset.section}`);
      if (!modal?.matches('product-modal') || dawnModals.has(modal)) return;
      const content = modal.querySelector('[role="document"]');
      const modalDialog = modal.querySelector('[role="dialog"]');
      if (!content || !modalDialog) return;
      dawnModals.add(modal);
      modal.classList.add('ab-vf-dawn-modal');
      const toolbar = document.createElement('div');
      toolbar.className = 'ab-vf-dawn-tools';
      toolbar.innerHTML = '<button type="button" data-vf-dawn-prev>Previous</button><button type="button" data-vf-dawn-zoom aria-pressed="false">Zoom in</button><button type="button" data-vf-dawn-next>Next</button><p aria-live="polite"></p>';
      content.before(toolbar);
      const zoomButton = toolbar.querySelector('[data-vf-dawn-zoom]');
      const modalCaption = toolbar.querySelector('p');
      // Dawn's media-modal pointerup handler otherwise closes before click
      // reaches these controls, and also closes at the end of a zoomed pan.
      toolbar.addEventListener('pointerup', event => event.stopPropagation());
      content.addEventListener('pointerup', event => {
        if (modal.classList.contains('is-zoomed')) event.stopPropagation();
      });
      const media = () => [...content.children].filter(child => child.hasAttribute('data-media-id'));
      const current = () => media().find(child => child.classList.contains('active'));
      const reset = () => {
        modal.classList.remove('is-zoomed');
        zoomButton.setAttribute('aria-pressed', 'false');
        zoomButton.textContent = 'Zoom in';
        content.scrollTop = content.scrollLeft = 0;
        const active = current();
        zoomButton.disabled = active?.tagName !== 'IMG';
        modalCaption.textContent = active?.getAttribute('alt') || active?.getAttribute('aria-label') || '';
      };
      const move = direction => {
        const sequence = media();
        if (!sequence.length) return;
        const activeIndex = sequence.indexOf(current());
        const next = sequence[(activeIndex + direction + sequence.length) % sequence.length];
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        // Dawn's public method reads the opener's data-media-id. A detached
        // proxy preserves the real opener, including its focus-return target.
        const realOpener = modal.openedBy;
        const proxy = document.createElement('button');
        proxy.setAttribute('data-media-id', next.dataset.mediaId);
        modal.openedBy = proxy;
        if (typeof modal.showActiveMedia === 'function') modal.showActiveMedia();
        modal.openedBy = realOpener;
        reset();
      };
      toolbar.addEventListener('click', event => {
        if (event.target.closest('[data-vf-dawn-prev]')) move(-1);
        if (event.target.closest('[data-vf-dawn-next]')) move(1);
        if (event.target.closest('[data-vf-dawn-zoom]')) {
          const isZoomed = modal.classList.toggle('is-zoomed');
          zoomButton.setAttribute('aria-pressed', String(isZoomed));
          zoomButton.textContent = isZoomed ? 'Fit image' : 'Zoom in';
          if (!isZoomed) content.scrollTop = content.scrollLeft = 0;
        }
      });
      modal.addEventListener('keydown', event => {
        if (event.key === 'Tab') {
          const focusable = [...modal.querySelectorAll('button:not([disabled]),a[href],input,select,textarea,[tabindex]:not([tabindex="-1"])')].filter(visible);
          const first = focusable[0], last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }
        if (!modal.classList.contains('is-zoomed') && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1);
        }
      });
      new MutationObserver(() => {
        if (modal.hasAttribute('open')) {
          reset();
          modal.querySelector('.product-media-modal__toggle')?.focus();
        }
      }).observe(modal, { attributes: true, attributeFilter: ['open'] });
    });
    setupDawnModals();
    document.addEventListener('shopify:section:load', setupDawnModals);
    /* A21: Dawn initPages returns before initializing offset/total for one
     * slide, while its bound scroll update still divides by that offset.
     * Complete only that scoped instance's one-page state; leave multi-slide
     * math and the accessible "of" text owned by the original component. */
    const blogSliders = new WeakSet();
    const setupBlogSliders = () => {
      customElements.whenDefined('slider-component').then(() => {
        document.querySelectorAll('.ab-canvas-blog-home slider-component').forEach(component => {
          if (blogSliders.has(component) || !component.slider || !component.nextButton) return;
          blogSliders.add(component);
          const singleArticleState = () => {
            if (component.sliderItems.length !== 1) return false;
            const article = component.sliderItems[0];
            component.sliderItemsToShow = [article];
            component.sliderItemOffset = Math.max(article.clientWidth, component.slider.clientWidth, 1);
            component.slidesPerPage = 1;
            component.totalPages = 1;
            component.currentPage = 1;
            if (component.currentPageElement) component.currentPageElement.textContent = '1';
            if (component.pageTotalElement) component.pageTotalElement.textContent = '1';
            if (component.prevButton) component.prevButton.disabled = true;
            component.nextButton.disabled = true;
            return true;
          };
          const nativeInitPages = component.initPages.bind(component);
          const nativeUpdate = component.update.bind(component);
          component.initPages = () => {
            if (!singleArticleState()) nativeInitPages();
          };
          component.update = () => {
            if (!singleArticleState()) nativeUpdate();
          };
          // The constructor already bound its original update listener. Keep
          // its input offset finite and finalize the one-page state afterward.
          component.slider.addEventListener('scroll', singleArticleState);
          component.initPages();
        });
      });
    };
    setupBlogSliders();
    document.addEventListener('shopify:section:load', setupBlogSliders);
    const observer = new MutationObserver(enhance);
    observer.observe(document.querySelector('main') || document.body, {
      subtree: true, childList: true, attributes: true,
      attributeFilter: ['src', 'srcset', 'alt', 'class', 'hidden', 'aria-hidden']
    });
    enhance();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
